import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  MenuItem,
  Select,
  Grid,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  Menu,
  Typography,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { LabelWithControl } from "components/common/LabelControl";
import {
  PAGE,
  PO_ELEMENTS,
  PRODUCT_DETAILS_TYPE,
  USER_ROLE,
} from "components/common/CONSTANTS";
import { useDispatch, useSelector } from "react-redux";
import { PO_SEARCH_OPPORTUNITIES_RESET } from "redux/actions/action-type";
import { SupplierSiteIdSearchFeild } from "components/common/SupplierSiteIdSearchFeild";
import { poSearchOpportunities } from "redux/actions/poSearchOpportunitiesAction";
import { getStoreCatalogueAction } from "redux/actions/createManualOrderStoresAction";
import { showNotificationDialog } from "redux/actions/notificationActions";
import { showErrorMessageByCode } from "services/commonServices";

export const CreateManualPO = () => {
  const userType = useSelector((state) => state.user);
  const categoryReducer = useSelector((state) => state.createManualOrderStores);
  const { accessiblePages, userDetailResponse } = userType.userType;
  const { rolePrivilegeDetails } = userDetailResponse;
  const [errors, setErrors] = useState({});
  const [categoryItems, setCategoryItems] = useState([]);
  const [categoryName, setCategoryName] = React.useState(["All"]);
  const [searchText, setSearchText] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { locationResponse } = useSelector((state) => state.locations);
  const [accessibleButtonlist, setAccessibleButtonlist] = useState([]);

  const [formData, setFormData] = useState({
    supplierSiteId: null,
    destinationLocation: null,
    orderDate: dayjs(),
    category: null,
    noOfOrders: "1",
    pinNumbers: null,
    isRoundingOn: false,
    suggestedOrderQty: false,
    stockHolding: null,
  });

  const [supplierDetails, setSupplierDetails] = useState();
  const [validDestinationLocations, setValidDestinationLocations] = useState(
    []
  );
  const [showSpinner, setShowSpinner] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    isPOSearchOpportunitiesLoading,
    isPOSearchOpportunitiesSuccess,
    isPOSearchOpportunitiesError,
    searchOpportunitiesError,
  } = useSelector((state) => state.poSearchOpportunities);

  const getDestination = (locationId) => {
    const destination = locationResponse.filter(
      (item) => item.locationId === locationId
    );
    return destination.length > 0 ? destination[0] : null;
  };

  useEffect(() => {
    setAccessibleButtonlist(
      accessiblePages[PAGE.UPLOAD_CREATE_MANUAL_ORDER_PO.key]
    );
  }, [accessiblePages]);

  useEffect(() => {
    setFormData({
      ...formData,
      isRoundingOn:
        rolePrivilegeDetails?.[0].roleName !== USER_ROLE.CENTRAL_IMPORTS,
    });
  }, [rolePrivilegeDetails]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const validLocations = locationResponse.map(
      (location) => location.locationId
    );
    setValidDestinationLocations(validLocations);
  }, [locationResponse]);

  useEffect(() => {
    dispatch({ type: PO_SEARCH_OPPORTUNITIES_RESET });
    dispatch(
      getStoreCatalogueAction(PRODUCT_DETAILS_TYPE.MANUAL_PURCHASE_ORDER)
    );
  }, [dispatch]);

  useEffect(() => {
    if (categoryReducer.isGetStoreCatalogueLoading) {
      setShowSpinner(true);
    } else if (categoryReducer.isGetStoreCatalogueSuccess) {
      const categorys = [];
      categoryReducer.getStoreCatalogueResponse.forEach((item) => {
        if (!categorys.includes(item.category)) {
          categorys.push(item.category);
        }
      });
      categorys.sort((a, b) => a.localeCompare(b));
      setCategoryItems(["All", ...categorys]);
      setCategoryName(["All"]);
      setShowSpinner(false);
    } else if (categoryReducer.isGetStoreCatalogueError) {
      setShowSpinner(false);
      if (categoryReducer.getStoreCatalogueResponse.response.status === 404) {
        const errTitle = "404 - Data not found";
        const errContent =
          categoryReducer.getStoreCatalogueResponse.response.data.errorMessage;
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
      } else {
        const { errTitle, errContent } = showErrorMessageByCode(
          categoryReducer.getStoreCatalogueResponse
        );
        dispatch(showNotificationDialog(errTitle, errContent, "Ok"));
      }
    }
  }, [categoryReducer, dispatch]);

  const handleCheckboxChange = (name) => {
    if (name === "All") {
      if (categoryName.includes("All")) {
        setCategoryName([]);
      } else {
        setCategoryName(["All"]);
      }
    } else {
      if (categoryName.includes(name)) {
        const newCategoryName = categoryName.filter((item) => item !== name);
        setCategoryName(newCategoryName);
      } else {
        const newCategoryName = [
          ...categoryName.filter((item) => item !== "All"),
          name,
        ];
        setCategoryName(newCategoryName);
      }
    }
  };

  useEffect(() => {
    if (isPOSearchOpportunitiesSuccess) {
      const noOfOrders = parseInt(formData.noOfOrders, 10);
      const categorys = categoryName.includes("All")
        ? "All"
        : categoryName.join(", ");

      sessionStorage.setItem("navigatedFromCreate", "true");

      navigate(PAGE.CREATE_MANUAL_ORDER_PO.url, {
        state: {
          noOfOrders,
          categorys,
        },
      });
    }
  }, [isPOSearchOpportunitiesSuccess, navigate, formData.noOfOrders]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPOSearchOpportunitiesError) {
      if (searchOpportunitiesError?.response?.data?.httpResponseCode === 404) {
        setErrorMessage(searchOpportunitiesError.response.data.errorMessage);
      } else {
        const { errContent } = showErrorMessageByCode(searchOpportunitiesError);
        setErrorMessage(errContent);
      }
    } else {
      setErrorMessage("");
    }
  }, [isPOSearchOpportunitiesError, searchOpportunitiesError]);

  const handleCategorySearch = (event) => {
    const { value } = event.target;
    setCategoryName(value);
  };

  const filteredNames = categoryItems.filter((name) => {
    const regex = new RegExp(searchText, "i");
    return regex.test(name);
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchText("");
  };

  const handleDestinationChange = (value) => {
    setFormData({ ...formData, destinationLocation: value });
    setErrors({
      ...errors,
      destinationLocation: !value ? "Destination Location is required" : "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDateChange = (date) => {
    const today = dayjs().startOf("day");
    if (!date || dayjs(date).isBefore(today)) {
      setErrors({ ...errors, orderDate: "Order date cannot be in the past" });
      setFormData({ ...formData, orderDate: null });
    } else {
      setErrors({ ...errors, orderDate: "" });
      setFormData({ ...formData, orderDate: date });
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.supplierSiteId)
      newErrors.supplierSiteId = "Supplier Site Id is required";

    if (!formData.destinationLocation) {
      newErrors.destinationLocation = "Destination Location is required";
    } else if (
      !validDestinationLocations.includes(
        parseInt(formData.destinationLocation)
      )
    ) {
      newErrors.destinationLocation = "Invalid Destination Location";
    }

    if (!formData.orderDate) newErrors.orderDate = "Order Date is required";
    if (
      !formData.noOfOrders ||
      isNaN(formData.noOfOrders) ||
      formData.noOfOrders < 1 ||
      formData.noOfOrders > 9
    ) {
      newErrors.noOfOrders = "Value must be between 1 and 9";
    }

    if (
      formData.suggestedOrderQty &&
      (!formData.stockHolding ||
        isNaN(formData.stockHolding) ||
        formData.stockHolding < 1 ||
        formData.stockHolding > 7)
    ) {
      newErrors.stockHolding = "Value must be between 1 and 7";
    }

    const pinNumbers = formData.pinNumbers
      ? formData.pinNumbers.split(",")
      : [];
    if (pinNumbers && pinNumbers.length > 10) {
      newErrors.pinNumbers = "PIN can have maximum of 10 entries";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const destination = getDestination(parseInt(formData.destinationLocation));
    const packIds = formData.pinNumbers
      ? formData.pinNumbers.split(",").map((pin) => Number(pin))
      : null;

    dispatch(
      poSearchOpportunities({
        supplierSiteId: formData.supplierSiteId,
        supplierName: supplierDetails.supplierName,
        shipToLocationId: formData.destinationLocation,
        shipToLocationName: destination.locationName,
        category: categoryName.includes("All") ? ["All"] : categoryName,
        packIds: packIds,
        orderDate: dayjs(formData.orderDate).format("YYYY-MM-DD"),
        isRoundingOn: formData.isRoundingOn,
        isSuggestedQuantity: formData.suggestedOrderQty,
        stockHoldingQuantity: formData.suggestedOrderQty
          ? parseInt(formData.stockHolding)
          : null,
      })
    );
  };

  return (
    <>
      {(isPOSearchOpportunitiesLoading || showSpinner) && (
        <Backdrop
          sx={{
            color: "#fff",
            flexDirection: "column",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={true}
        >
          <CircularProgress color="inherit" />

          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Loading...
          </Typography>
        </Backdrop>
      )}
      <div className="labelControl">
        <Grid
          container
          spacing={2}
          style={{ marginBottom: "5px", fontSize: "15px" }}
        >
          <Grid item xs={12} sm={4} md={1.5}>
            <SupplierSiteIdSearchFeild
              isRequired={true}
              onValueChange={(event, newValue) => {
                setSupplierDetails(newValue);
                setFormData({
                  ...formData,
                  supplierSiteId: newValue ? newValue.id : "",
                });
              }}
              error={!formData.supplierSiteId ? errors?.supplierSiteId : ""}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            <LabelWithControl
              label={
                <>
                  Destination Location <span style={{ color: "red" }}>*</span>
                </>
              }
            >
              <TextField
                name="destinationLocation"
                variant="outlined"
                size="small"
                placeholder="Destination Location"
                value={formData.destinationLocation}
                onChange={(e) =>
                  handleDestinationChange(
                    e.target.value.replace(/[^0-9 ]/g, "")
                  )
                }
                error={!!errors.destinationLocation}
                helperText={errors.destinationLocation}
                fullWidth
                style={{ marginBottom: "10px" }}
              />
            </LabelWithControl>
          </Grid>
          <Grid item xs={12} sm={4} md={1.25}>
            <LabelWithControl
              label={
                <>
                  Order Date <span style={{ color: "red" }}>*</span>
                </>
              }
            >
              <DatePicker
                format="DD/MM/YYYY"
                value={formData.orderDate}
                defaultValue={dayjs()}
                disablePast
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    size: "small",
                    placeholder: "Order Date",
                    error: !!errors.orderDate,
                    helperText: errors.orderDate,
                    onKeyDown: (e) => e.preventDefault(),
                    inputProps: { 'data-testid': 'order-date-input' },
                  },
                }}
                fullWidth
                style={{ marginBottom: "10px" }}
              />
            </LabelWithControl>
          </Grid>
          <Grid item xs={12} sm={4} md={1.5}>
            <LabelWithControl label="Category">
              <Select
                className="dropDownValue"
                id="searchable-multiple-checkbox"
                multiple
                value={categoryName}
                onChange={handleCategorySearch}
                // renderValue={(selected) => selected.join(", ")}
                renderValue={(selected) =>
                  selected.includes("All") ? "All" : selected.join(", ")
                }
                open={Boolean(anchorEl)}
                onOpen={handleClick}
                onClose={handleClose}
                MenuComponent={(props) => <Menu {...props} />}
              >
                {filteredNames.length > 0 ? (
                  filteredNames.map((name) => (
                    <MenuItem
                      key={name}
                      value={name}
                      className="filteredNameListStyle"
                    >
                      <Checkbox
                        checked={categoryName.includes(name)}
                        onChange={() => handleCheckboxChange(name)}
                      />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem className="filteredNameListStyle" disabled>
                    No results found
                  </MenuItem>
                )}
              </Select>
            </LabelWithControl>
          </Grid>
          <Grid item xs={12} sm={4} md={1}>
            <LabelWithControl label="No of Orders">
              <TextField
                name="noOfOrders"
                variant="outlined"
                size="small"
                value={formData.noOfOrders}
                onChange={handleChange}
                error={!!errors.noOfOrders}
                helperText={errors.noOfOrders}
                fullWidth
                style={{ marginBottom: "10px" }}
                placeholder="Enter 1-9"
              />
            </LabelWithControl>
          </Grid>
          <Grid item xs={12} sm={4} md={1.75}>
            <LabelWithControl label="PIN">
              <TextField
                name="pinNumbers"
                variant="outlined"
                size="small"
                value={formData.pinNumbers}
                onChange={handleChange}
                error={!!errors.pinNumbers}
                helperText={errors.pinNumbers}
                fullWidth
                style={{ marginBottom: "10px" }}
              />
            </LabelWithControl>
          </Grid>
          <Grid item xs={12} sm={6} md={1.25}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isRoundingOn}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      isRoundingOn: e.target.checked,
                    })
                  }
                  color="primary"
                />
              }
              className="switchLabelText"
              label="Rounding"
              labelPlacement="start"
              style={{ fontSize: "11px", fontWeight: "bold", marginLeft: 0 }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.25}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.suggestedOrderQty}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      suggestedOrderQty: e.target.checked,
                    })
                  }
                  color="primary"
                />
              }
              className="switchLabelText"
              label="Suggest Order Qty"
              labelPlacement="start"
              sx={{ fontSize: "12px", fontWeight: "bold", marginLeft: 0 }}
            />

            {formData.suggestedOrderQty && (
              <TextField
                name="stockHolding"
                variant="outlined"
                size="small"
                placeholder="Stock Holding (days)"
                value={formData.stockHolding}
                onChange={handleChange}
                error={!!errors.stockHolding}
                helperText={errors.stockHolding}
                fullWidth
                style={{
                  marginBottom: "10px",
                  display: "block",
                  width: "135px",
                }}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={4} md={1}>
            {accessibleButtonlist.includes(
              PO_ELEMENTS.UPLOAD_PO.SUBMIT_BUTTON
            ) && (
              <Button
                className="commonPOButtons"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                style={{
                  marginTop: "18px",
                  marginRight: "8px",
                }}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
        {errorMessage && <Typography color="red">{errorMessage}</Typography>}
      </div>
    </>
  );
};
